import type { CrucibleConnectionStatus } from '@sorrem/shared';
import type { UIGameState } from '@sorrem-client/types';
import { useGameplay } from '@sorrem-client/ui/features/PlayPage/gameplay-context';
import { isShallowEqual, useGameSelector } from '@sorrem-client/ui/hooks';

export interface RunLoadingGate {
    /** Whether the loading view covers the screen. */
    isLoadingScreenShown: boolean;
    /** The loading bar's fill, 0 to 1. */
    loadingScreenProgress: number;
    /** What the loading view says while this client is loaded and the party is not; null otherwise. */
    waitingMessage: string | null;
    /** Whether the run's HUD is up. */
    isHudShown: boolean;
    /** When the countdown before the run comes alive ends, while it runs; null otherwise. */
    countdownEndsAtMs: number | null;
}

interface RunLoadingGateInput {
    readonly loadingProgress: UIGameState['loadingProgress'];
    readonly crucibleSessionState: UIGameState['crucibleSessionState'];
    /** Where the loading bar starts: the share of the crucible loaded before the run took over. */
    readonly loadingProgressStart: number;
    /** A debug preview (CharacterDebugPage/DebugOverlayPage) has no live session and always shows the HUD. */
    readonly isDebugPreview: boolean;
}

/** How many of the roster are at each connection step, in one pass. */
function countByConnectionStatus(roster: readonly { readonly connectionStatus: CrucibleConnectionStatus }[]): Record<CrucibleConnectionStatus, number> {
    const counts: Record<CrucibleConnectionStatus, number> = { 'waiting-to-connect': 0, connected: 0, loading: 0, ready: 0 };
    for (const entry of roster) {
        counts[entry.connectionStatus] += 1;
    }
    return counts;
}

/** What the loading view says while this client waits on the rest of the party. */
function describePartyWaiting(counts: Record<CrucibleConnectionStatus, number>, rosterSize: number): string {
    if (counts.loading > 0) {
        return `Players loading: ${counts.loading}/${rosterSize}`;
    }
    const joinedCount = counts.connected + counts.ready;
    if (joinedCount === 0) {
        return 'Waiting for all players to join...';
    }
    return counts['waiting-to-connect'] > 0
        ? `Players joined: ${joinedCount}/${rosterSize}. Waiting for remaining players...`
        : 'Preparing all players for loading...';
}

/**
 * Whether the loading view or the HUD shows. Until the run is under way the
 * player sees the loading view: while this client loads, then full, waiting
 * on the rest of the party.
 */
export function resolveRunLoadingGate({ loadingProgress, crucibleSessionState, loadingProgressStart, isDebugPreview }: RunLoadingGateInput): RunLoadingGate {
    // A debug preview's loadingProgress/crucibleSessionState stay at their
    // defaults, so the gate is skipped entirely and the HUD always renders.
    const countdownEndsAtMs = !isDebugPreview && crucibleSessionState?.phase === 'countdown'
        ? crucibleSessionState.countdownEndsAtMs
        : null;
    const rosterEntries = crucibleSessionState?.roster ?? [];
    const isRunUnderWay = crucibleSessionState?.phase === 'countdown' || crucibleSessionState?.phase === 'playing';
    const isLoadingScreenShown = !isDebugPreview && (loadingProgress !== null || !isRunUnderWay);
    const loadingScreenProgress = loadingProgressStart + (1 - loadingProgressStart) * (loadingProgress ?? (isRunUnderWay ? 1 : 0));
    const waitingMessage = !isLoadingScreenShown || loadingProgress !== null || crucibleSessionState === null
        ? null
        : describePartyWaiting(countByConnectionStatus(rosterEntries), rosterEntries.length);
    const isHudShown = isDebugPreview || (loadingProgress === null && isRunUnderWay);

    return { isLoadingScreenShown, loadingScreenProgress, waitingMessage, isHudShown, countdownEndsAtMs };
}

type RunLoadingGateView = RunLoadingGate & {
    /** Whether the game has a crucible session (a debug preview has none): the party panel needs one. */
    hasCrucibleSession: boolean;
};

/**
 * The run's loading gate, from the game's loading progress and the crucible
 * session; see `resolveRunLoadingGate`. Selects the gate itself, so the
 * overlay re-renders when it changes, not on every roster update.
 */
export function useRunLoadingGate(loadingProgressStart: number): RunLoadingGateView {
    const { bridge, debugControls } = useGameplay();
    const isDebugPreview = debugControls !== undefined;
    return useGameSelector(bridge, (state) => ({
        ...resolveRunLoadingGate({ loadingProgress: state.loadingProgress, crucibleSessionState: state.crucibleSessionState, loadingProgressStart, isDebugPreview }),
        hasCrucibleSession: state.crucibleSessionState !== null,
    }), isShallowEqual);
}
